import { fmtMoney, fmtNum, fmtPct, fmtPctPlain, trendColor } from "@/lib/format";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import CountUp from "./CountUp";

const VARIANTS = {
    default: { border: "border-fp-border", accent: "#0A2A22", chip: null },
    primary: { border: "border-l-4 border-l-fp-signal border-fp-border", accent: "#1F5F5B", chip: null },
    success: { border: "border-l-4 border-l-fp-success border-fp-border", accent: "#2D6A4F", chip: null },
    plum:    { border: "border-l-4 border-l-fp-plum border-fp-border",    accent: "#7B3F5C", chip: null },
    teal:    { border: "border-l-4 border-l-fp-teal border-fp-border",    accent: "#3B7A73", chip: null },
    warn:    { border: "border-l-4 border-l-fp-saffron border-fp-border", accent: "#E19825", chip: null },
    danger:  { border: "border-l-4 border-l-fp-terracotta border-fp-border", accent: "#C05E4C", chip: null },
};

export const KpiCard = ({ label, value, unit, delta, deltaLabel, trend, subValue, testId, variant = "default", chip, rawValue, decimals, prefix, valueSuffix }) => {
    const isPositive = delta >= 0;
    const arrow = delta == null ? Minus : (isPositive ? TrendUp : TrendDown);
    const Arrow = arrow;
    const v = VARIANTS[variant] || VARIANTS.default;
    const data = trend?.map((val, i) => ({ i, v: val })) || [];
    const gradId = `grad-${(testId || label || "x").replace(/[^a-z0-9]/gi, "")}`;

    // If rawValue is provided, use animated CountUp; else fall back to pre-formatted string
    const displayValue = rawValue != null
        ? <CountUp value={rawValue} decimals={decimals ?? 0} prefix={prefix || ""} suffix={valueSuffix || ""} />
        : value;

    return (
        <div className={`bg-white border ${v.border} p-4 sm:p-5 lg:p-6 hover:shadow-sm transition-all group relative overflow-hidden min-w-0`} data-testid={testId}>
            <div className="flex items-start justify-between mb-3 gap-2">
                <div className="font-sans text-[10px] uppercase tracking-[0.18em] text-fp-muted leading-tight break-words">{label}</div>
                {chip && <span className="shrink-0 px-1.5 py-0.5 font-mono text-[9px] tracking-wider bg-fp-ink text-white uppercase">{chip}</span>}
            </div>
            <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <div className="font-mono text-2xl sm:text-3xl tracking-tight text-fp-ink break-all">{displayValue}</div>
                {unit && <div className="font-mono text-sm text-fp-muted">{unit}</div>}
            </div>
            {subValue && <div className="font-mono text-[11px] text-fp-secondary mb-3">{subValue}</div>}
            {delta != null && (
                <div className={`inline-flex items-center gap-1.5 font-mono text-xs mb-1 ${trendColor(delta)}`}>
                    <Arrow size={12} weight="bold" />
                    <span>{fmtPct(delta)}</span>
                    {deltaLabel && <span className="text-fp-muted ml-1">{deltaLabel}</span>}
                </div>
            )}
            {data.length > 1 && (
                <div className="h-12 -mx-2 mt-3 opacity-90 group-hover:opacity-100 transition-opacity">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data}>
                            <defs>
                                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={v.accent} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={v.accent} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <Area type="monotone" dataKey="v" stroke={v.accent} strokeWidth={1.8} fill={`url(#${gradId})`} isAnimationActive={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export { fmtMoney, fmtNum, fmtPct, fmtPctPlain };
